import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage, IMG } from "@/i18n";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ArticleCard from "@/components/ArticleCard";
import { api } from "@/lib/api";

function HeroPortrait() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const { t } = useLanguage();
  return (
    <motion.div ref={ref} style={{ y }} className="relative h-full" data-testid="hero-portrait">
      <div className="relative overflow-hidden h-[420px] sm:h-[520px] lg:h-[640px]">
        <img
          src={IMG.portrait}
          alt={t.hero.portraitCaption}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-brand/10 mix-blend-multiply" />
      </div>
      <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-slate-400">
        {t.hero.portraitCaption}
      </p>
    </motion.div>
  );
}

export default function Home() {
  const { lang, t } = useLanguage();
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles").then((res) => setArticles(res.data.slice(0, 3))).catch(() => {});
  }, []);

  const treatments = t.treatments.items.map((item, i) => ({
    ...item,
    image: [IMG.homeopathy, IMG.clinic, IMG.nutrition][i],
  }));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="min-h-screen flex items-center pt-24 sm:pt-28 pb-16 bg-white">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center w-full">
          <div className="lg:col-span-7">
            <MaskedLine className="mb-6">
              <p className="eyebrow">{t.hero.eyebrow}</p>
            </MaskedLine>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-slate-900 tracking-tight">
              <MaskedLine delay={0.12}>{t.hero.line1}</MaskedLine>
              <MaskedLine delay={0.26}>
                <span className="italic text-brand">{t.hero.line2}</span>
              </MaskedLine>
              <MaskedLine delay={0.4}>{t.hero.line2accent}</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 max-w-xl text-base sm:text-lg font-light leading-relaxed text-slate-600"
            >
              {t.hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/contatti"
                data-testid="hero-cta-booking"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-medium text-white hover:bg-brand-dark transition-colors duration-300"
              >
                {t.hero.cta1} <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/trattamenti"
                data-testid="hero-cta-treatments"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 text-sm font-medium text-slate-800 hover:border-brand hover:text-brand transition-colors duration-300"
              >
                {t.hero.cta2}
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <HeroPortrait />
          </div>
        </div>
      </section>

      {/* DISCIPLINES STRIP */}
      <section className="border-y border-slate-200 bg-brand-linen" data-testid="disciplines-strip">
        <div className="container-editorial grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
          {t.treatments.items.map((item, i) => (
            <div key={item.name} className="py-8 sm:px-8 first:sm:pl-0 flex items-baseline gap-4">
              <span className="font-serif italic text-brand/60 text-lg">0{i + 1}</span>
              <div>
                <p className="font-serif text-xl sm:text-2xl text-slate-900">{item.name}</p>
                <p className="mt-1 text-xs font-light text-slate-500">{item.tagline}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Marquee items={t.marquee} />

      {/* ABOUT */}
      <section className="py-20 sm:py-28 lg:py-32 bg-white" data-testid="about-section">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="md:col-span-5">
            <div className="overflow-hidden">
              <img src={IMG.studio} alt={t.about.eyebrow} className="w-full h-[380px] sm:h-[460px] object-cover" />
            </div>
          </Reveal>
          <div className="md:col-span-7">
            <Reveal>
              <p className="eyebrow">{t.about.eyebrow}</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-tight text-slate-900 tracking-tight">
                {t.about.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base font-light leading-relaxed text-slate-600">{t.about.body1}</p>
              <p className="mt-4 text-base font-light leading-relaxed text-slate-600">{t.about.body2}</p>
              <blockquote className="mt-8 border-l-2 border-brand pl-6 font-serif italic text-xl sm:text-2xl text-brand">
                "{t.about.quote}"
              </blockquote>
              <Link
                to="/curriculum"
                data-testid="about-cta-curriculum"
                className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-brand link-underline"
              >
                {t.about.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TREATMENTS */}
      <section className="py-20 sm:py-28 lg:py-32 bg-brand-linen" data-testid="treatments-section">
        <div className="container-editorial">
          <Reveal>
            <p className="eyebrow">{t.treatments.eyebrow}</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-tight text-slate-900 tracking-tight">
              {t.treatments.title}
            </h2>
            <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-slate-600">{t.treatments.sub}</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {treatments.map((item, i) => (
              <Reveal key={item.name} delay={i * 0.1} className="h-full">
                <Link
                  to="/trattamenti"
                  data-testid={`treatment-card-${i}`}
                  className="group flex flex-col h-full bg-white border border-slate-200 hover:border-brand/50 transition-colors duration-300"
                >
                  <div className="overflow-hidden h-56">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex flex-col flex-1 p-7">
                    <span className="font-serif italic text-brand/50">{`0${i + 1}`}</span>
                    <h3 className="mt-2 font-serif text-2xl text-slate-900 group-hover:text-brand transition-colors duration-300">
                      {item.name}
                    </h3>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">{item.tagline}</p>
                    <p className="mt-4 text-sm font-light leading-relaxed text-slate-600">{item.description}</p>
                    <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-brand link-underline">
                      {t.treatments.cta} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ARTICLES PREVIEW */}
      <section className="py-20 sm:py-28 lg:py-32 bg-white" data-testid="articles-preview-section">
        <div className="container-editorial">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">{t.articles.eyebrow}</p>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight">
                {t.articles.title}
              </h2>
            </div>
            <Link
              to="/articoli"
              data-testid="articles-preview-cta"
              className="inline-flex items-center gap-2 text-sm font-medium text-brand link-underline"
            >
              {t.articles.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {articles.length === 0 && (
              <p className="text-sm font-light text-slate-500">{t.articles.loading}</p>
            )}
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.1} className="h-full">
                <ArticleCard article={a} testid={`article-preview-card-${i}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white" data-testid="contact-cta-section">
        <div className="container-editorial py-20 sm:py-28 text-center">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] text-white/60">{t.contacts.eyebrow}</p>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight">
              {t.contacts.title}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-white/70">
              {t.contacts.sub}
            </p>
            <Link
              to="/contatti"
              data-testid="contact-cta-button"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-medium text-brand hover:bg-brand-soft transition-colors duration-300"
            >
              {t.nav.prenota} <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
